import React from 'react';

export interface Option {
  value: string;
  label: string;
}

export interface CheckboxGroupFieldProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: readonly Option[];
  error?: string;
  required?: boolean;
  name?: string;
  disabled?: boolean;
}

/**
 * Reusable checkbox group field component
 * Stores array of selected option keys
 */
export const CheckboxGroupField: React.FC<CheckboxGroupFieldProps> = ({
  label,
  value,
  onChange,
  options,
  error,
  required = false,
  name,
  disabled = false,
}) => {
  const fieldName = name || `checkbox-group-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const fieldsetId = `${fieldName}-fieldset`;
  const errorId = `${fieldName}-error`;
  const selectedValues = Array.isArray(value) ? value : [];

  const handleChange = (optionValue: string, checked: boolean) => {
    if (disabled) return;
    
    if (checked) {
      onChange([...selectedValues, optionValue]);
    } else {
      onChange(selectedValues.filter((v) => v !== optionValue));
    }
  };

  return (
    <div style={{ marginBottom: '15px' }}>
      <fieldset
        id={fieldsetId}
        style={{
          padding: '15px',
          border: error ? '1px solid #dc3545' : '1px solid #ccc',
          borderRadius: '4px',
        }}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? errorId : undefined}
      >
        <legend style={{ fontWeight: required ? 'bold' : 'normal', padding: '0 8px' }}>
          {label}
          {required && <span style={{ color: 'red', marginLeft: '4px' }} aria-label="required">*</span>}
        </legend>
        {options.map((option) => {
          const optionId = `${fieldName}-${option.value}`;
          const isChecked = selectedValues.includes(option.value);
          
          return (
            <label
              key={option.value}
              htmlFor={optionId}
              style={{
                display: 'block',
                marginBottom: '8px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              <input
                id={optionId}
                type="checkbox"
                name={fieldName}
                value={option.value}
                checked={isChecked}
                onChange={(e) => handleChange(option.value, e.target.checked)}
                disabled={disabled}
                style={{ marginRight: '8px' }}
              />
              {option.label}
            </label>
          );
        })}
      </fieldset>
      {error && (
        <div
          id={errorId}
          role="alert"
          style={{
            color: '#dc3545',
            fontSize: '12px',
            marginTop: '4px',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

